import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { TenantConfigViewData, ViewDefnState } from 'src/dao/tenantConfigClient';
import { AppState, AppThunkDispatch } from 'src/store';
import { ViewDataState } from 'src/types/Domain';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import service from 'src/ServiceContainer';
import Axios from 'src/services/axios';
import { BasicItem, ListDataOptions } from 'src/worker/pivotWorker.types';
import { getSelectedFloorsetIndex } from './FloorsetComparison.selectors';
import { isEmpty, isEqual, isNil, set } from 'lodash';
import { cacheCheckFetchPivotData } from 'src/pages/Hindsighting/StyleColorReview/StyleColorReview.slice';
import { ComponentErrorType, ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ColumnGroupedViewComponent, ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { receiveTopMemberDropdownData, updateTopMemberSelection } from 'src/components/Subheader/Subheader.slice';
import { maybeGetComponentProps } from 'src/utils/Functions/epicsFunctions';

export interface FloorsetComparisonGroupingInfo {
  dataIndex: string;
  staticColumns: string[];
}

export interface FloorsetComparisonTenantResponse {
  viewDefn: TenantConfigViewData;
  unmodifiedViewDefn?: TenantConfigViewData;
  calcViewDefn?: TenantConfigViewData;
  favoritesList?: FavoriteResponseItem[];
}

// Reducer types

export interface FloorsetCmpSlice {
  viewDefn: TenantConfigViewData;
  unmodifiedViewDefn: TenantConfigViewData;
  calcViewDefn: TenantConfigViewData;
  groupingInfo?: FloorsetComparisonGroupingInfo | null;
  floorsets: BasicItem[];
  selectedFloorsetIndex: number;
  floorsetIndexLastUpdated: number;
  favoritesList: FavoriteResponseItem[];
  viewDefnState: ViewDefnState;
  viewDataState: ViewDataState;
  cacheHash: string | null;
}
const initialState: FloorsetCmpSlice = {
  viewDefn: {} as TenantConfigViewData,
  unmodifiedViewDefn: {} as TenantConfigViewData,
  calcViewDefn: {} as TenantConfigViewData,
  groupingInfo: null,
  floorsets: [],
  selectedFloorsetIndex: -1,
  floorsetIndexLastUpdated: Date.now(),
  favoritesList: [],
  viewDefnState: ViewDefnState.idle,
  viewDataState: ViewDataState.idle,
  cacheHash: null,
};

const floorsetComparisonReducer = createSlice({
  name: 'FloorsetComparison',
  initialState,
  reducers: {
    requestTenantConfig(state) {
      state.viewDefnState = ViewDefnState.loading;
    },
    receiveTenantConfig(state, action: PayloadAction<FloorsetComparisonTenantResponse>) {
      state.viewDefnState = ViewDefnState.loaded;
      const payload = action.payload;
      state.viewDefn = payload.viewDefn;
      if (!isNil(payload.unmodifiedViewDefn)) state.unmodifiedViewDefn = payload.unmodifiedViewDefn;
      if (!isNil(payload.calcViewDefn)) state.calcViewDefn = payload.calcViewDefn;
      if (!isNil(payload.favoritesList)) state.favoritesList = payload.favoritesList;
    },
    requestData(state) {
      state.viewDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveCacheHash(state, action: PayloadAction<string>) {
      state.cacheHash = action.payload;
    },
    receiveCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.cacheHash) {
        state.viewDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.cacheHash) {
        state.viewDataState = ViewDataState.liveDataReady;
      }
    },
    setGroupingInfo(state, action: PayloadAction<FloorsetComparisonGroupingInfo>) {
      state.groupingInfo = action.payload;
    },
    receiveError(_state, _action: PayloadAction<ErrorBoundaryComponentError>) {
      return initialState;
    },
    cleanUp() {
      return initialState;
    },
  },
});

export const {
  requestTenantConfig,
  receiveTenantConfig,
  requestData,
  receiveCacheHash,
  receiveCachedData,
  receiveLiveData,
  setGroupingInfo,
  receiveError,
  cleanUp,
} = floorsetComparisonReducer.actions;

export function fetchFloorsetComparisonData(modelDefn: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(modelDefn, options),
    requestData,
    receiveCacheHash,
    receiveCachedData,
    receiveLiveData
  );
}

export function fetchFloorsetsAndSelect() {
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<AnyAction | void> => {
    const maybeComponentProps = maybeGetComponentProps<ColumnGroupedViewComponent>(
      getState(),
      ConfDefnComponentType.floorsetComparison
    );
    const productId = getState().scope.scope.productMember;
    if (!productId) {
      throw new Error('Product member should exist but doesnt, this shouldnt happen');
    }
    let floorsets: any[] = [];
    const existingFloorsets = getState().subheader.topMemberOptions;

    if (maybeComponentProps?.floorsetApi) {
      // copied from configurablegrid.slice
      const { url: floorsetUrl, headers: floorsetHeaders, params: floorsetParams } = maybeComponentProps?.floorsetApi;
      const floorsetOptions = {
        headers: floorsetHeaders,
        params: floorsetParams,
      };
      const currentAppName = getState().perspective.selected?.appType;
      if (!currentAppName) throw new Error('AppName not found, this should not happen');
      set(floorsetOptions, 'params.appName', currentAppName);
      floorsets = (await Axios.get(floorsetUrl, floorsetOptions)).data.data;
    } else {
      const url = `/api/assortment/floorsets?appName=${ASSORTMENT}&product=${encodeURIComponent(productId)}`;
      floorsets = (await Axios.get(url)).data.data;
    }

    if (!isEmpty(floorsets)) {
      // Compare floorsets before updating, incase they're still valid
      if (!isEqual(existingFloorsets, floorsets)) {
        // Floorsets have changed; update the state
        dispatch(receiveTopMemberDropdownData(floorsets));
        const selectedIndex = getSelectedFloorsetIndex(getState());
        const selection = floorsets[selectedIndex]?.id || floorsets[0].id; // Default to first item if index is invalid
        dispatch(updateTopMemberSelection(selection));
      } else {
        // Floorsets are the same; ensure selection is valid
        const currentSelection = getState().subheader.topMemberSelection;
        const isSelectionValid = floorsets.some((floorset) => floorset.id === currentSelection);

        if (!isSelectionValid) {
          // Selection is invalid; update to a default value
          const selection = floorsets[0].id;
          dispatch(updateTopMemberSelection(selection));
        } else if (currentSelection) dispatch(updateTopMemberSelection(currentSelection));
      }
    } else {
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: 'No floorsets configured for this view',
          name: ConfDefnComponentType.floorsetComparison,
        })
      );
    }
  };
}

export default floorsetComparisonReducer.reducer;
